import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Detect if we have real credentials
const isConfigured = 
  supabaseUrl !== '' && 
  supabaseAnonKey !== '' && 
  !supabaseUrl.includes('your-project-ref') && 
  !supabaseAnonKey.includes('your-anon-key-here');

export const supabase = isConfigured ? createClient(supabaseUrl, supabaseAnonKey) : null;

// Helpers to read/write fallback data using localStorage
const FALLBACK_KEY_PREFIX = 'bytepairs_fallback_';

export const fallbackDb = {
  async insert(tableName, data) {
    const records = this.getRecords(tableName);
    const newRecord = {
      id: Math.random().toString(36).substring(2, 11),
      createdAt: new Date().toISOString(),
      ...data
    };
    records.unshift(newRecord);
    localStorage.setItem(FALLBACK_KEY_PREFIX + tableName, JSON.stringify(records));
    return { data: [newRecord], error: null };
  },

  async select(tableName) {
    const records = this.getRecords(tableName);
    return { data: records, error: null };
  },

  async delete(tableName, id) {
    let records = this.getRecords(tableName);
    records = records.filter(rec => rec.id !== id);
    localStorage.setItem(FALLBACK_KEY_PREFIX + tableName, JSON.stringify(records));
    return { error: null };
  },

  async update(tableName, id, updatedData) {
    let records = this.getRecords(tableName);
    let index = records.findIndex(rec => rec.id === id);
    if (index !== -1) {
      records[index] = {
        ...records[index],
        ...updatedData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(FALLBACK_KEY_PREFIX + tableName, JSON.stringify(records));
      return { data: [records[index]], error: null };
    }
    return { data: null, error: 'Record not found' };
  },

  getRecords(tableName) {
    try {
      const dataStr = localStorage.getItem(FALLBACK_KEY_PREFIX + tableName);
      let records = dataStr ? JSON.parse(dataStr) : [];
      // Clean up and filter out any previously seeded blogs that exist
      if (tableName === 'blogs') {
        const filtered = records.filter(rec => rec && rec.id && !rec.id.startsWith('seed-'));
        if (filtered.length !== records.length) {
          localStorage.setItem(FALLBACK_KEY_PREFIX + tableName, JSON.stringify(filtered));
          records = filtered;
        }
      }
      return records;
    } catch (e) {
      console.error("Error reading fallback storage", e);
      return [];
    }
  }
};

// Universal insert interface
export const dbInsert = async (tableName, data) => {
  if (supabase) {
    try {
      const { data: result, error } = await supabase
        .from(tableName)
        .insert([data])
        .select();
      if (!error) return { data: result, error: null };
      console.warn("Supabase database insert failed, fallback to local storage:", error);
    } catch (e) {
      console.warn("Supabase insert connection error, fallback to local storage:", e);
    }
  }
  return await fallbackDb.insert(tableName, data);
};

// Universal select interface
export const dbSelect = async (tableName, orderByField = 'createdAt', orderAscending = false) => {
  if (supabase) {
    try {
      const { data: result, error } = await supabase
        .from(tableName)
        .select('*')
        .order(orderByField, { ascending: orderAscending });
      if (!error) return { data: result, error: null };
      console.warn("Supabase database select failed, fallback to local storage:", error);
    } catch (e) {
      console.warn("Supabase select connection error, fallback to local storage:", e);
    }
  }
  return await fallbackDb.select(tableName);
};

// Universal update interface
export const dbUpdate = async (tableName, id, data) => {
  if (supabase) {
    try {
      const { data: result, error } = await supabase
        .from(tableName)
        .update(data)
        .eq('id', id)
        .select();
      if (!error) return { data: result, error: null };
      console.warn("Supabase database update failed, fallback to local storage:", error);
    } catch (e) {
      console.warn("Supabase update connection error, fallback to local storage:", e);
    }
  }
  return await fallbackDb.update(tableName, id, data);
};

// Universal delete interface
export const dbDelete = async (tableName, id) => {
  if (supabase) {
    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id);
      if (!error) return { error: null };
      console.warn("Supabase database delete failed, fallback to local storage:", error);
    } catch (e) {
      console.warn("Supabase delete connection error, fallback to local storage:", e);
    }
  }
  return await fallbackDb.delete(tableName, id);
};

// Sync localStorage data to Supabase
const TABLES_TO_SYNC = [
  'submissions',
  'project_inquiries',
  'consultations',
  'blogs',
  'blog_comments',
  'blog_categories',
  'blog_tags',
  'newsletter_subscriptions'
];

export const syncLocalStorageToSupabase = async () => {
  if (!supabase) {
    console.log("Supabase is not configured yet. Cannot sync local storage.");
    return { success: false, message: 'Supabase not configured' };
  }

  console.log("Starting localStorage to Supabase sync...");
  let syncedCount = 0;

  for (const tableName of TABLES_TO_SYNC) {
    const localRecords = fallbackDb.getRecords(tableName);
    if (localRecords && localRecords.length > 0) {
      try {
        console.log(`Syncing ${localRecords.length} records for ${tableName}...`);
        
        // Use upsert to avoid duplicate key errors if some records were partially synced
        const { error } = await supabase
          .from(tableName)
          .upsert(localRecords);

        if (!error) {
          localStorage.removeItem(FALLBACK_KEY_PREFIX + tableName);
          syncedCount += localRecords.length;
          console.log(`Successfully synced ${localRecords.length} records for ${tableName}`);
        } else {
          console.warn(`Failed to sync ${tableName} to Supabase:`, error);
        }
      } catch (err) {
        console.error(`Error syncing table ${tableName}:`, err);
      }
    }
  }

  return { success: true, syncedCount };
};
